import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Trash2, Eye, Flag, TrendingDown, BarChart3, Shield } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import StatsCard from './StatsCard';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const FlaggedContent = ({ API_BASE }) => {
  const [flaggedPosts, setFlaggedPosts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchFlaggedPosts();
  }, [statusFilter]);

  const fetchFlaggedPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/admin/flagged-posts`, {
        params: { status: statusFilter }
      });
      setFlaggedPosts(response.data.flagged_posts || []);
    } catch (error) {
      console.error('Error fetching flagged posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (action) => {
    try {
      await axios.post(`${API_BASE}/api/admin/flagged-posts/${selectedFlag.id}/resolve`, null, {
        params: { action, admin_notes: adminNotes }
      });
      
      alert(`Flag ${action === 'delete' ? 'resolved - post deleted' : 'dismissed - post kept'}!`);
      setShowResolveModal(false);
      setSelectedFlag(null);
      setAdminNotes('');
      fetchFlaggedPosts();
    } catch (error) {
      console.error('Error resolving flag:', error);
      alert('Failed to resolve flag');
    }
  };

  const getReasonBadge = (reason) => {
    const badges = {
      'Spam': 'bg-red-500/20 text-red-400',
      'Inappropriate Content': 'bg-orange-500/20 text-orange-400',
      'Harassment': 'bg-purple-500/20 text-purple-400',
      'Misinformation': 'bg-yellow-500/20 text-yellow-400',
      'Other': 'bg-gray-500/20 text-gray-400'
    };
    return badges[reason] || badges.Other;
  };

  // Calculate moderation statistics
  const calculateModerationStats = () => {
    const totalFlags = flaggedPosts.length;
    const pendingFlags = flaggedPosts.filter(f => f.status === 'pending').length;
    const resolvedFlags = flaggedPosts.filter(f => f.status === 'resolved').length;
    const dismissedFlags = flaggedPosts.filter(f => f.status === 'dismissed').length;
    
    // Calculate flags in last 24 hours
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const recentFlags = flaggedPosts.filter(f => new Date(f.flagged_at) >= oneDayAgo).length;
    
    // Calculate response time (mock data for now)
    const avgResponseTime = '2.4 hrs';
    
    return {
      totalFlags,
      pendingFlags,
      resolvedFlags,
      dismissedFlags,
      recentFlags,
      avgResponseTime
    };
  };

  // Prepare reason distribution data for chart
  const getReasonDistribution = () => {
    const reasonCounts = {};
    flaggedPosts.forEach(flag => {
      reasonCounts[flag.reason] = (reasonCounts[flag.reason] || 0) + 1;
    });
    
    const colors = {
      'Spam': '#EF4444',
      'Inappropriate Content': '#F97316', 
      'Harassment': '#8B5CF6',
      'Misinformation': '#F59E0B',
      'Other': '#6B7280'
    };
    
    return Object.entries(reasonCounts).map(([reason, count]) => ({
      name: reason,
      value: count,
      color: colors[reason] || colors.Other
    }));
  };

  // Prepare flags over time data for trend chart
  const getFlagsTrendData = () => {
    const dailyData = {};
    
    flaggedPosts.forEach(flag => {
      const date = new Date(flag.flagged_at).toLocaleDateString('en', { month: 'short', day: 'numeric' });
      dailyData[date] = (dailyData[date] || 0) + 1;
    });
    
    // Get last 7 days
    const result = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStr = date.toLocaleDateString('en', { month: 'short', day: 'numeric' });
      result.push({
        date: dateStr,
        flags: dailyData[dateStr] || 0
      });
    }
    
    return result;
  };

  // Prepare status distribution data for pie chart
  const getStatusDistribution = () => {
    const stats = calculateModerationStats();
    return [
      { name: 'Pending', value: stats.pendingFlags, color: '#F59E0B' },
      { name: 'Resolved', value: stats.resolvedFlags, color: '#EF4444' },
      { name: 'Dismissed', value: stats.dismissedFlags, color: '#10B981' }
    ];
  };

  const moderationStats = calculateModerationStats();
  const reasonData = getReasonDistribution();
  const trendData = getFlagsTrendData();
  const statusData = getStatusDistribution();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="heading-2 text-white">Content Moderation</h2>
        <div className="text-gray-400">Flagged Posts: {flaggedPosts.length}</div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Flags"
          value={moderationStats.totalFlags}
          icon={<Flag size={24} />}
          color="red"
        />
        <StatsCard
          title="Pending Review"
          value={moderationStats.pendingFlags}
          icon={<AlertTriangle size={24} />}
          color="yellow"
        />
        <StatsCard
          title="24h Activity"
          value={moderationStats.recentFlags}
          icon={<TrendingDown size={24} />}
          color="purple"
          trend="recent flags"
        />
        <StatsCard
          title="Avg Response Time"
          value={moderationStats.avgResponseTime}
          icon={<Shield size={24} />}
          color="turquoise"
        />
      </div>

      {/* Charts Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Flags Trend Chart */}
        <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
          <h3 className="text-white text-lg font-semibold mb-4">Flags Trend (7 Days)</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
              <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="flags" 
                stroke="#EF4444" 
                strokeWidth={3}
                dot={{ fill: '#EF4444', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Flag Reasons Chart */}
        <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
          <h3 className="text-white text-lg font-semibold mb-4">Flag Reasons Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={reasonData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {reasonData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Legend 
                wrapperStyle={{ color: '#9CA3AF' }}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Status Overview Chart */}
      <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
        <h3 className="text-white text-lg font-semibold mb-4">Moderation Status Overview</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={statusData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="name" stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <YAxis stroke="#9CA3AF" style={{ fontSize: '12px' }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1F2937', 
                border: '1px solid #374151',
                borderRadius: '8px',
                color: '#fff'
              }}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {statusData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
        >
          <option value="pending">Pending Review</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
          <option value="all">All Flags</option>
        </select>
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="text-center p-8 text-gray-400">Loading flagged posts...</div>
        ) : flaggedPosts.length === 0 ? (
          <div className="text-center p-8 text-gray-400">
            {statusFilter === 'pending' ? 'No posts pending review' : 'No flagged posts found'}
          </div>
        ) : (
          flaggedPosts.map((flag) => (
            <div key={flag.id} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle className="text-red-400" size={20} />
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getReasonBadge(flag.reason)}`}>
                      {flag.reason}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {new Date(flag.flagged_at).toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="text-gray-400 text-sm mb-2">
                    By: <span className="text-white">{flag.post_author}</span>
                  </div>
                  
                  {flag.details && (
                    <div className="text-gray-400 text-sm mb-3">
                      Reporter notes: <span className="text-gray-300">{flag.details}</span>
                    </div>
                  )}
                </div>

                {flag.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedFlag(flag);
                        setShowResolveModal(true);
                      }}
                      className="p-2 text-turquoise-400 hover:bg-turquoise-500/10 rounded-lg"
                      title="Review"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-gray-800 rounded-lg p-4 border-l-4 border-red-500">
                <p className="text-gray-300 whitespace-pre-wrap">{flag.post_content}</p>
              </div>

              {flag.admin_notes && (
                <div className="mt-3 text-sm text-gray-400">
                  Admin notes: <span className="text-gray-300">{flag.admin_notes}</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showResolveModal && selectedFlag && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-16 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="heading-3 text-white mb-4">Review Flagged Post</h3>
            
            <div className="space-y-4 mb-6">
              <div>
                <div className="text-sm text-gray-400 mb-2">Reason:</div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getReasonBadge(selectedFlag.reason)}`}>
                  {selectedFlag.reason}
                </span>
              </div>

              {selectedFlag.details && (
                <div>
                  <div className="text-sm text-gray-400 mb-2">Reporter Details:</div>
                  <div className="text-gray-300">{selectedFlag.details}</div>
                </div>
              )}

              <div>
                <div className="text-sm text-gray-400 mb-2">Post Content:</div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-300 whitespace-pre-wrap">{selectedFlag.post_content}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Admin Notes (Optional)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this decision..."
                  rows="3"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  dir="ltr"
                  style={{ direction: 'ltr' }}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => handleResolve('delete')}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                <Trash2 size={16} className="mr-2" />
                Delete Post
              </Button>
              <Button
                onClick={() => handleResolve('dismiss')}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
              >
                <CheckCircle size={16} className="mr-2" />
                Keep Post
              </Button>
              <Button
                onClick={() => {
                  setShowResolveModal(false);
                  setSelectedFlag(null);
                  setAdminNotes('');
                }}
                className="btn-secondary"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlaggedContent;
